import { Button, Input, InputRef, Popover, Typography } from 'antd'
import { TooltipPlacement } from 'antd/lib/tooltip'
import { ChangeEventHandler, FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useClickAway, useKey } from 'react-use'
import styled from 'styled-components'

import { Placement } from './utils'

interface DictionaryPopupProps {
  position?: {
    dimension: {
      top: number | string
      bottom: number | string
      left: number | string
      right: number | string
    }
    placement: Placement
  }
  onClose: () => void
  onClear: (selectedWord: string) => void
  onSave: (selectedWord: string, dictionaryWord: string) => void
  selectedWord: string
  dictionaryWord: string
}

const DictionaryPopup: FC<DictionaryPopupProps> = ({
  position,
  onClose,
  onClear,
  onSave,
  selectedWord,
  dictionaryWord,
}) => {
  const { t } = useTranslation('common')

  const [inputtedDictionaryWord, setInputtedDictionaryWord] =
    useState(dictionaryWord)

  useEffect(() => {
    setInputtedDictionaryWord(dictionaryWord)
  }, [dictionaryWord])

  const handleChangeDictionaryWord: ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    setInputtedDictionaryWord(e.target.value)
  }

  const handleClear = () => {
    onClear(selectedWord)
    onClose()
  }

  const handleSave = () => {
    const trimmedDictionaryWord = inputtedDictionaryWord.trim()

    if (!trimmedDictionaryWord) {
      return
    }

    onSave(selectedWord, trimmedDictionaryWord)
    onClose()
  }

  const handleClose = () => {
    onClose()
  }

  const inputRef = useRef<InputRef>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [dictionaryWord, position])

  /**
   * 팝업 이외의 영역을 클릭했을 때 닫히게 하는 로직
   */
  const popoverRef = useRef<HTMLElement | null>(null)
  const POPOVER_CLASS_NAME = 'pause-popover'

  useEffect(() => {
    popoverRef.current = document.querySelector<HTMLElement>(
      `.${POPOVER_CLASS_NAME}`,
    )
  }, [])

  useClickAway(popoverRef, handleClose, ['pointerdown'])
  useKey('Escape', handleClose)

  return (
    <Popover
      overlayClassName={POPOVER_CLASS_NAME}
      arrow={true}
      open={true}
      overlayStyle={{
        ...position?.dimension,
      }}
      placement={position?.placement}
      content={() => {
        return (
          <DictionaryPopupContainer>
            <Typography.Title level={5}>
              {t('Adjust Pronunciation')}
            </Typography.Title>
            <Input
              size="large"
              placeholder={t('Adjust here')}
              onChange={handleChangeDictionaryWord}
              onPressEnter={handleSave}
              value={inputtedDictionaryWord}
              ref={inputRef}
            />
            <FooterButtons>
              {/* 
                TODO: 가이드 기능이 확정되면 다시 작업 필요함
              */}
              {/* <Button
                type="link"
                size="large"
              >{t('Guide')}</Button> */}

              <div className="clear-save-buttons">
                <Button
                  type="default"
                  size="large"
                  danger
                  onClick={handleClear}>
                  {t('Clear Changes')}
                </Button>
                <Button type="primary" size="large" onClick={handleSave}>
                  {t('Save')}
                </Button>
              </div>
            </FooterButtons>
          </DictionaryPopupContainer>
        )
      }}
    />
  )
}

export default DictionaryPopup

const DictionaryPopupContainer = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;

  width: 300px;
`

const FooterButtons = styled.div`
  display: flex;
  justify-content: end;

  .clear-save-buttons {
    display: flex;
    gap: 8px;
  }
`
